import { List } from 'immutable';
import RelationAbbreviationControl from 'modules/Abbreviations/components/ToolbarAbbreviation/RelationAbbreviationControl';
import React from 'react';
import Subtitle from 'components/Toolbar/BaseToolbar/components/Subtitle';
import { HOVER_HINT } from 'components/Toolbar/constants';
import AltTag from 'components/Toolbar/Controls/AltTag';
import AssetAlignment from 'components/Toolbar/Controls/AssetAlignment/List';
import BackgroundImage from 'components/Toolbar/Controls/BackgroundImage';
import Border from 'components/Toolbar/Controls/Border';
import BorderRadius from 'components/Toolbar/Controls/BorderRadius';
import FillColor from 'components/Toolbar/Controls/FillColor';
import ImageMobileSettings from 'components/Toolbar/Controls/ImageMobileSettings';
import Link from 'components/Toolbar/Controls/Link';
import Padding from 'components/Toolbar/Controls/Padding';
import AutoFitContentToggle from 'components/Toolbar/Shared/AutoFitContentToggle';
import ControlGroup from 'components/Toolbar/Shared/ControlGroup';
import HeightWidth from 'components/Toolbar/Shared/HeightWidth';
import { AccessibilityRole, MinCellWidth, ProjectsConfig } from 'const';
import { allowImageMobileSettings } from 'utils/allowImageMobileSettings';
import { intlGet } from 'utils/intlGet';
import BaseToolbar from '../BaseToolbar';
import AccessibilityRoleControl, { AccessibilityOption } from '../Controls/AccessibilityRole';
import { ToolbarProps } from './models';

const IMAGE_ACCESSIBILITY_OPTIONS: AccessibilityOption[] = [
  {
    value: AccessibilityRole.PRESENTATION,
    text: intlGet('AccessibilityRole.ImageCell', AccessibilityRole.PRESENTATION),
  },
  {
    value: AccessibilityRole.NORMAL,
    text: intlGet('AccessibilityRole.ImageCell', AccessibilityRole.NORMAL),
  },
];

const Toolbar: React.FunctionComponent<ToolbarProps> = (props) => {
  const {
    styles,
    setters,
    alignmentDisabled,
    altTagDisabled,
    cellHeight,
    cellWidth,
    disableCellWidthEditing,
    height,
    isIncreasingWidthDisabled,
    layoutId,
    linkDisabled,
    maxCellHeight,
    maxContentHeight,
    maxContentWidth,
    minCellHeight,
    mobileSettingsDisabled,
    projectType,
    rootDocument,
    toggleCellHeight,
    toggleColumnWidth,
    width,
    toggleAutoFitContent,
    isAutoFitContent,
    changeAccessibilityRole,
    currentAccessibilityRole,
  } = props;
  const { allowBackgroundImage, allowBackgroundOpacity } = ProjectsConfig[projectType];

  return (
    <BaseToolbar layoutId={layoutId} dataTestId='imageToolbar-baseToolbar'>
      <Subtitle title={intlGet('EditorToolbar.Asset', 'Cell')} dataTestId='imageToolbar-subtitle-cell' />
      <ControlGroup dataTestId='controlGroup0'>
        <HeightWidth
          height={cellHeight}
          maxHeight={maxCellHeight}
          minHeight={minCellHeight}
          toggleHeight={toggleCellHeight}
          disableCellWidthEditing={disableCellWidthEditing}
          isIncreasingWidthDisabled={isIncreasingWidthDisabled}
          minWidth={MinCellWidth.IMAGE}
          width={cellWidth}
          toggleWidth={toggleColumnWidth}
          toggleColumnWidth={toggleColumnWidth}
          toggleAutoFitContent={toggleAutoFitContent}
          isAutoFitContent={isAutoFitContent}
          dataTestId='imageToolbar-heightWidth-cell'
        />
        <AutoFitContentToggle
          isAutoFitContent={isAutoFitContent}
          isAutoHeight={false}
          toggleAutoFitContent={toggleAutoFitContent}
          dataTestId='imageToolbar-autoFit'
        />
      </ControlGroup>
      <Subtitle title={intlGet('EditorToolbar.Asset', 'Image')} />
      <ControlGroup dataTestId='controlGroup1'>
        <HeightWidth
          width={width}
          maxWidth={maxContentWidth}
          toggleWidth={setters.width}
          height={height}
          maxHeight={maxContentHeight}
          toggleHeight={setters.height}
          dataTestId='imageToolbar-heightWidth-image'
        />
      </ControlGroup>
      {allowImageMobileSettings(projectType, rootDocument) &&
        <ControlGroup dataTestId='controlGroup2'>
          <ImageMobileSettings
            alignmentDisabled={alignmentDisabled}
            alignment={styles.mobileAlignment}
            mobileSettingsApplied={styles.mobileSettingsApplied}
            isDisabled={mobileSettingsDisabled}
            scale={styles.mobileScale}
            mobileViewImage={styles.mobileViewImage}
            toggleAlignment={setters.mobileAlignment}
            toggleScale={setters.mobileScale}
            toggleMobileSettingsApplied={setters.mobileSettingsApplied}
            toggleMobileViewImage={setters.mobileViewImage}
            dataTestId='imageToolbar-imageMobileSetting'
          />
        </ControlGroup>
      }
      <ControlGroup dataTestId='controlGroup3'>
        <AssetAlignment
          isDisabled={alignmentDisabled}
          alignment={styles.alignment}
          toggleAlignment={setters.alignment}
          dataTestId='imageToolbar-assetAlignment'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup4'>
        <Padding padding={styles.padding} togglePadding={setters.padding} dataTestId='imageToolbar-padding' />
        <Border border={styles.border} toggleBorder={setters.border} dataTestId='imageToolbar-border' />
        <FillColor
          activeColor={styles.backgroundColor}
          toggleColor={setters.backgroundColor}
          opacity={styles.backgroundColorOpacity}
          toggleOpacity={allowBackgroundOpacity ? setters.backgroundColorOpacity : undefined}
          gradient={styles.backgroundGradient}
          toggleGradient={setters.backgroundGradient}
          dataTestId='imageToolbar-fillColor'
        />
        {allowBackgroundImage && <BackgroundImage
          backgroundImage={styles.backgroundImage}
          toggleBackgroundImage={setters.backgroundImage}
          dataTestId='imageToolbar-backgroundImage'
        />}
        <BorderRadius
          borderRadius={styles.borderRadius}
          title={HOVER_HINT.CELL}
          dropdownWarningMessage={intlGet('EditorToolbar.Tooltip', 'WarningMessage')}
          toggleBorderRadius={setters.borderRadius}
          dataTestId='imageToolbar-borderRadius'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup5'>
        <AltTag
          isDisabled={altTagDisabled}
          altTag={styles.altTag}
          toggleAltTag={setters.altTag}
          dataTestId='imageToolbar-altTag'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup6'>
        <Link
          isDisabled={Boolean(linkDisabled)}
          link={styles.link}
          toggleLink={setters.link}
          dataTestId='imageToolbar-link'
        />
        <RelationAbbreviationControl
          selectedAbbreviationIDs={styles.abbreviations?.toArray() ?? []}
          onChange={(value): void => setters.abbreviations(List<string>(value))}
          dataTestId='imageToolbar-relationAbbreviation'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup7'>
        <AccessibilityRoleControl
          accessibilityOptions={IMAGE_ACCESSIBILITY_OPTIONS}
          currentAccessibilityRole={currentAccessibilityRole}
          changeAccessibilityRole={changeAccessibilityRole}
          dataTestId='imageToolbar-accessibilityRole'
        />
      </ControlGroup>
    </BaseToolbar>
  );
};

export default Toolbar;
